import * as React from "react";

/**
 * Minimal, XSS-safe rich text. HTML is escaped first, then a small markdown
 * subset is applied — **bold**, *italic*, [text](https://…) links, `- ` bullet
 * lists, and line breaks. Because escaping happens before any tag is injected,
 * and link hrefs are constrained to http(s) by the pattern, no raw or hostile
 * HTML survives.
 */
export function RichText({ text, className }: { text: string; className?: string }) {
  const html = React.useMemo(() => toSafeHtml(text), [text]);
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Inline spans: links, then bold, then italic. Applied after escaping. */
function inline(s: string): string {
  return s
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer noopener" class="text-teal-700 underline">$1</a>',
    )
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
}

/**
 * Block-aware so `- ` lines become a real <ul> instead of dash-prefixed text.
 * Consecutive bullet lines group into one list; everything else is a paragraph
 * run whose lines are joined with <br/>. Blocks are emitted in source order.
 */
function toSafeHtml(raw: string): string {
  const lines = escapeHtml(raw).split("\n");
  const blocks: string[] = [];
  let para: string[] = [];
  let list: string[] = [];
  const flushPara = () => {
    if (para.length) {
      blocks.push(para.map(inline).join("<br/>"));
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push(
        `<ul class="my-1 list-disc space-y-0.5 pl-5">${list
          .map((t) => `<li>${inline(t)}</li>`)
          .join("")}</ul>`,
      );
      list = [];
    }
  };
  for (const line of lines) {
    const bullet = /^[-*] +(.*)$/.exec(line);
    if (bullet) {
      flushPara();
      list.push(bullet[1]);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return blocks.join("");
}
